import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { isStaffAllowedPath } from "@/lib/staffAccess";

interface AdminRouteProps {
  children: ReactNode;
}

/**
 * Guards Super Admin routes (staff: pages allowed for their roles only).
 */
export const AdminRoute = ({ children }: AdminRouteProps) => {
  const { isSuperAdmin, loading: roleLoading, isActuallySuperAdmin, isActuallyStaff, staffRoles, isImpersonating } = useWorkspace();
  const { loading: authLoading, user } = useAuth();
  const location = useLocation();

  // 1. Either layer still resolving → wait.
  if (authLoading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // 2. Auth finished but no user → ProtectedRoute will handle the redirect.
  //    Render a passive spinner so we don't briefly redirect to "/" ourselves.
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // 3. Definitive check. Staff may open the pages of their roles
  //    (the server enforces the same split).
  if (!isSuperAdmin && !isActuallySuperAdmin) {
    if (isActuallyStaff && !isImpersonating) {
      if (isStaffAllowedPath(location.pathname, staffRoles)) return <>{children}</>;
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
